import { invariant } from '@seen/core/invariant'
import type { InitialCarePlan } from '@seen/core/models/care-plans/InitialCarePlan'
import { CarePlanParticipantSignatureTask } from '@seen/core/models/tasks/signature/carePlanParticipantSignatureTask'
import { formatReference, parseReferenceString } from '@seen/core/utils'
import { Button } from '@seen/ui'
import { IconPlus } from '@tabler/icons-react'
import { type JSX, useMemo } from 'react'
import { ResourceFieldForm } from '#src/components/core/form/ResourceFieldForm.tsx'
import { DateTimeDisplay } from '#src/components/display/DateTimeDisplay.tsx'
import { ExtendedResourceTable } from '#src/components/fhir-table/ExtendedResourceTable.tsx'
import type { ColumnConfig } from '#src/components/fhir-table/FhirMRTTable.constants.tsx'
import { PractitionerNameplate } from '#src/components/nameplates/PractitionerNameplate.tsx'
import { OwnerComboboxButton } from '#src/components/OwnerComboboxButton.tsx'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { SignatureStatus } from '#src/components/signature/SignatureStatus.tsx'
import { useCreateMutation } from '#src/hooks/useCreateMutation.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'

export function CarePlanParticipantReview({
  carePlan,
}: {
  carePlan: InitialCarePlan
}): JSX.Element {
  const practitioner = useCurrentPractitioner()
  const isReadOnly = useIsReadOnly()

  const columnConfigs = useMemo<
    ColumnConfig<CarePlanParticipantSignatureTask>[]
  >(
    () => [
      {
        id: 'name',
        headerText: 'Reviewer',
        render: (signature) => {
          if (signature.isDone) {
            invariant(
              signature.ownerPractitioner,
              'Signature is done but no practitioner owner'
            )
            return (
              <PractitionerNameplate
                practitioner={signature.ownerPractitioner}
              />
            )
          }
          return (
            <ResourceFieldForm
              instance={signature}
              valueFn={(s) => s.ownerPractitioner?.reference ?? null}
              patchFn={(s, owner) => {
                s.ownerPractitioner =
                  owner === null ? undefined : parseReferenceString(owner)
              }}
              render={({ field }) => <OwnerComboboxButton {...field} />}
            />
          )
        },
        valueType: 'text',
        getSortFilterValue: (signature) =>
          formatReference(signature.ownerPractitioner),
        filteringType: 'text',
        enableSorting: true,
      },
      {
        id: 'dateSigned',
        headerText: 'Review Date',
        render: (signature) => (
          <DateTimeDisplay value={signature.completionDate} format="MM/dd/yy" />
        ),
        valueType: 'date' as const,
        getSortFilterValue: (signature) => signature.completionDate,
        filteringType: 'date-range',
        enableSorting: true,
        useAsInitialSort: true,
        sortDescFirst: true,
      },
      {
        id: 'notes',
        headerText: 'Notes',
        render: (signature) => (
          <RichTextEditor
            value={signature.description}
            editable={false}
            variant="unstyled"
          />
        ),
        valueType: 'text',
        getSortFilterValue: (signature) => signature.description,
        filteringType: 'text',
        enableSorting: true,
      },
      {
        id: 'status',
        headerText: 'Status',
        render: (signature) => (
          <SignatureStatus signature={signature} isReview withConfirmation />
        ),
        valueType: 'text',
        getSortFilterValue: (signature) => signature.state,
        filteringType: 'text',
        enableSorting: true,
      },
    ],
    []
  )

  const createSignatureMutation =
    useCreateMutation<CarePlanParticipantSignatureTask>()

  const requestParticipantSignature = async (): Promise<void> => {
    createSignatureMutation.mutate(
      new CarePlanParticipantSignatureTask({
        carePlan,
        requester: practitioner.reference,
        signer: practitioner.reference,
      })
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-row items-center justify-between">
        <h2>Care Plan Review with Participant</h2>
        {!isReadOnly && (
          <Button
            onClick={requestParticipantSignature}
            variant="outline"
            size="sm"
          >
            <IconPlus className="mr-2 h-4 w-4" />
            Add Review
          </Button>
        )}
      </div>
      <ExtendedResourceTable
        resourceClass={CarePlanParticipantSignatureTask}
        filterQuery={CarePlanParticipantSignatureTask.byCarePlanReference({
          carePlan: carePlan.reference,
        })}
        columnConfigs={columnConfigs}
      />
    </div>
  )
}
